import { useQuery } from '@tanstack/react-query'
import type {
  IInstrumentTemplateDetail,
  IInstrumentTemplateListResponse,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'

export const platformInstrumentTemplateKeys = {
  all: ['platform-instrument-templates'] as const,
  list: (search: string) =>
    [...platformInstrumentTemplateKeys.all, 'list', search] as const,
  detail: (templateId: string) =>
    [...platformInstrumentTemplateKeys.all, 'detail', templateId] as const,
}

export async function fetchPlatformInstrumentTemplates(
  search: string,
  signal?: AbortSignal,
): Promise<IInstrumentTemplateListResponse> {
  const q = search.trim()
  const { data: envelope } = await apiClient.get<
    ApiResponse<IInstrumentTemplateListResponse>
  >('/api/v1/platform/instrument-templates', {
    signal,
    params: q ? { search: q } : undefined,
  })
  if (!envelope?.data) {
    throw new Error('Platform instrument templates response missing data')
  }
  return envelope.data
}

export async function fetchPlatformInstrumentTemplateById(
  templateId: string,
  signal?: AbortSignal,
): Promise<IInstrumentTemplateDetail> {
  const { data: envelope } = await apiClient.get<ApiResponse<IInstrumentTemplateDetail>>(
    `/api/v1/platform/instrument-templates/${encodeURIComponent(templateId)}`,
    { signal },
  )
  if (!envelope?.data?.id) {
    throw new Error('Platform instrument template response missing data')
  }
  return envelope.data
}

export function usePlatformInstrumentTemplates(search: string) {
  return useQuery({
    queryKey: platformInstrumentTemplateKeys.list(search),
    queryFn: ({ signal }) => fetchPlatformInstrumentTemplates(search, signal),
  })
}

export function usePlatformInstrumentTemplateDetail(
  templateId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: platformInstrumentTemplateKeys.detail(templateId ?? ''),
    queryFn: ({ signal }) => fetchPlatformInstrumentTemplateById(templateId!, signal),
    enabled: enabled && Boolean(templateId),
  })
}
